/**
 * V2 tutorial lessons — the coach-mark walkthrough layer from V1
 * (tutorial-checklist.js:620-957). Tapping a checklist item opens a
 * popup explaining WHY the step matters and HOW to do it, with a
 * "Show me" button that pulses the real UI element — chaining through
 * multi-step sequences (e.g. open a card, then tap Share) by pulsing
 * each target until it's clicked, polling for async-rendered ones.
 *
 * Targets are V2 selectors; the why/how copy is V1's, reworded where
 * the V2 chrome differs. Steps complete from performing the real
 * action (wired in discover/nav/share/etc.), never from the lesson.
 */
import { escapeHtml } from '../../../assets/js/utils.js';
import { showToast } from '../../ui/toast.js';

/**
 * Per-step lesson metadata, keyed by tutorial STEPS id.
 * target: selector to pulse · steps: chained sequence · login: needs a
 * session · requiresGrid: flips Discover to grid view before pulsing ·
 * tool: which toolbar tray the target lives in (2026-07-27 makeover,
 * widened to tablets 2026-08-06: below the toolbar breakpoint Discover's
 * tools hide in trays until their toolbar icon is tapped — the lesson
 * opens the tray first).
 */
export const LESSONS = {
  browse_categories: {
    emoji: '🔍',
    why: 'Categories are how adventures are organized — national parks, breweries, lighthouses, and more.',
    how: 'Tap the "All categories →" chip in the category row.',
    target: '[data-browse-cats]',
    requiresGrid: true, // the chip row only renders in grid mode
    tool: 'filter',
  },
  follow_category: {
    emoji: '➕',
    why: 'Following categories lets you filter the feed to just the adventures you care about.',
    how: 'Open All categories, then tap Follow on any category.',
    login: true,
    requiresGrid: true, // step 1's chip only renders in grid mode
    tool: 'filter',
    steps: [
      { target: '[data-browse-cats]', hint: 'Tap All categories to browse' },
      { target: '[data-follow-cat]', hint: 'Now tap Follow on any category!' },
    ],
  },
  view_category: {
    emoji: '📂',
    why: 'Besides the categories page, you can jump to a category from any yoink card — try it now!',
    how: 'Tap the category name on any yoink card below.',
    target: '.v2-card [data-category-slug]',
    requiresGrid: true,
    requiresCard: true,
  },
  switch_view: {
    emoji: '🗺️',
    why: 'Grid view shows photos and details. Map view shows where everything is geographically.',
    how: 'Tap the Grid/Map toggle in Discover’s view tools.',
    // Pulse the INACTIVE side: tapping the already-active button switches
    // nothing, and the step only completes on a real switch
    target: '[data-view-mode][aria-pressed="false"]',
    tool: 'view',
  },
  view_yoink: {
    emoji: '👀',
    why: 'Opening a yoink shows you the full photo, who logged it, where it is on the map, and more.',
    how: 'Tap any card in the grid to open it.',
    target: '.v2-card',
    requiresGrid: true,
    requiresCard: true,
  },
  go_home: {
    emoji: '🌐',
    why: 'Discover is home base — it resets your filters. Tap it anytime you get lost or want a fresh start.',
    how: 'Tap Discover in the navigation bar.',
    target: '[data-nav="discover"]',
  },
  switch_scope: {
    emoji: '⭐',
    why: 'Once you follow categories, Discover opens on just your followed adventures — this toggle switches between them and everything.',
    how: 'Tap the All/Following toggle in Discover’s view tools.',
    login: true,
    // The inactive side, not literally "following": the checklist shows
    // in the Following scope too, where the switch back to All is the tap
    // that demonstrates (and completes) this step
    target: '[data-feed-scope][aria-pressed="false"]',
    tool: 'view',
  },
  register: {
    emoji: '🔑',
    why: 'Signing in lets you log your own adventures, track progress, and build your journal.',
    how: 'Tap the Sign in button at the top-right.',
    target: '.v2-btn-signin',
  },
  log_yoink: {
    emoji: '📝',
    why: 'This is the heart of Yoink — snap a photo to prove you were there and add it to your journal.',
    // Personal categories (§11) ride as a byline, not their own step: the
    // curated taxonomy is the mainline, and the picker's own no-match
    // nudge does the teaching at exactly the right moment.
    how: 'Tap Log a yoink in the navigation bar, pick a place and the closest category, add your photo, and submit! (Nothing fits? The picker can file it under a personal category of your own instead.)',
    login: true,
    target: '[data-nav="log"]',
  },
  share: {
    emoji: '📤',
    why: 'Sharing sends a direct link to a specific yoink, category, or page to your friends.',
    how: 'Open any yoink card, tap Share, then Share link.',
    requiresGrid: true,
    requiresCard: true,
    // Share actions collapsed into a "Share ▾" pulldown (2026-07-27) —
    // the Share link row hides until the menu is opened
    steps: [
      { target: '.v2-card', hint: 'Tap any yoink card to open it' },
      { target: '.v2-share-menu-btn', hint: 'Now tap Share to open the menu' },
      { target: '.v2-btn-share', hint: 'Now tap Share link to share this yoink!' },
    ],
  },
  share_profile: {
    emoji: '👤',
    why: 'Your journal shows all your yoinks in one place — share it so friends can see your adventures.',
    how: 'Open My journal, tap Share, then Share link.',
    login: true,
    steps: [
      { target: '[data-nav="journal"]', hint: 'Tap My journal in the navigation bar' },
      { target: '.v2-share-menu-btn', hint: 'Now tap Share to open the menu' },
      { target: '.v2-btn-share', hint: 'Now tap Share link to share your journal!' },
    ],
  },
  open_how_it_works: {
    emoji: '📖',
    // Invited-only since 2026-07-27: the checklist no longer sits on
    // Discover uninvited — the lit Help button carries the invitation
    why: 'The Help button stays lit until you finish these lessons — they’re one tap away whenever you want them.',
    how: 'Tap Help in the navigation bar, then Tutorial.',
    steps: [
      { target: '[data-nav="help"]', hint: 'Tap Help in the navigation bar' },
      { target: '[data-nav="tutorial"]', hint: 'Now tap Tutorial' },
    ],
  },
};

// ---------------------------------------------------------------------------
// Coach-mark popup
// ---------------------------------------------------------------------------

let coachMarkEl = null;

/**
 * Open the lesson for a checklist step: coach-mark popup, then "Show me"
 * pulses the target. `ctx` carries { signedIn, viewMode, label }.
 */
export function openLesson(stepId, ctx = {}) {
  const lesson = LESSONS[stepId];
  if (!lesson) return;
  const label = ctx.label || stepId;

  if (stepId === 'register' && ctx.signedIn) {
    showCoachMark(lesson, label, {
      overrideHow: 'You’re already signed in — nice! This one’s done.',
      actionLabel: null,
    });
    return;
  }
  if (lesson.login && !ctx.signedIn) {
    showCoachMark(lesson, label, {
      overrideHow: 'Sign in first, then come back to try this!',
      action: () => pulseElement('.v2-btn-signin'),
      actionLabel: 'Show me Sign in',
    });
    return;
  }
  showCoachMark(lesson, label, {
    action: () => triggerPulse(lesson, ctx),
    actionLabel: 'Show me',
  });
}

function showCoachMark(lesson, label, { action, actionLabel = 'Show me', overrideHow } = {}) {
  dismissCoachMark();

  const howText = overrideHow || lesson.how;
  coachMarkEl = document.createElement('div');
  coachMarkEl.className = 'tutorial-coach-mark';
  coachMarkEl.setAttribute('role', 'dialog');
  coachMarkEl.setAttribute('aria-label', label);
  coachMarkEl.innerHTML = `
    <div class="tutorial-coach-mark-backdrop"></div>
    <div class="tutorial-coach-mark-card">
      <div class="tutorial-coach-mark-header">
        <span class="tutorial-coach-mark-emoji" aria-hidden="true">${lesson.emoji}</span>
        <span class="tutorial-coach-mark-title">${escapeHtml(label)}</span>
        <button type="button" class="tutorial-coach-mark-close" aria-label="Close">&times;</button>
      </div>
      ${lesson.why ? `<p class="tutorial-coach-mark-why">${escapeHtml(lesson.why)}</p>` : ''}
      <p class="tutorial-coach-mark-how">${escapeHtml(howText)}</p>
      <div class="tutorial-coach-mark-actions">
        ${actionLabel ? `<button type="button" class="tutorial-coach-mark-btn">${escapeHtml(actionLabel)}</button>` : ''}
        <button type="button" class="tutorial-coach-mark-dismiss">Got it</button>
      </div>
    </div>`;

  coachMarkEl.querySelector('.tutorial-coach-mark-backdrop').addEventListener('click', dismissCoachMark);
  coachMarkEl.querySelector('.tutorial-coach-mark-close').addEventListener('click', dismissCoachMark);
  coachMarkEl.querySelector('.tutorial-coach-mark-dismiss').addEventListener('click', dismissCoachMark);
  const showMeBtn = coachMarkEl.querySelector('.tutorial-coach-mark-btn');
  if (showMeBtn && action) {
    showMeBtn.addEventListener('click', () => {
      dismissCoachMark();
      setTimeout(action, 200); // let the popup finish closing first
    });
  }
  coachMarkEl.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') dismissCoachMark();
  });

  document.body.appendChild(coachMarkEl);
  requestAnimationFrame(() => coachMarkEl?.classList.add('active'));
  (showMeBtn || coachMarkEl.querySelector('.tutorial-coach-mark-dismiss'))?.focus();
}

export function dismissCoachMark() {
  if (!coachMarkEl) return;
  coachMarkEl.classList.remove('active');
  const el = coachMarkEl;
  coachMarkEl = null;
  setTimeout(() => el.remove(), 200); // fade-out transition
}

// ---------------------------------------------------------------------------
// Pulse engine (V1 tutorial-checklist.js:780-957)
// ---------------------------------------------------------------------------

function triggerPulse(lesson, ctx) {
  // Lessons that point at grid cards need grid view up first
  if (lesson.requiresGrid && ctx.viewMode === 'map') {
    const gridBtn = document.querySelector('[data-view-mode="grid"]');
    if (gridBtn) {
      gridBtn.click();
      setTimeout(() => triggerPulseInner(lesson), 600); // wait for the grid render
      return;
    }
  }
  triggerPulseInner(lesson);
}

// Must match the breakpoint that reveals `.discover-body .v2-toolbar`
// in components.css — below it, Discover's tools hide in trays. Was
// 560px until 2026-08-06, when the tablet got the toolbar too; the
// stale value left iPad-width lessons pulsing inside a closed tray.
const TRAY_TOOLBAR = '(max-width: 1199px)';

/**
 * Below the toolbar breakpoint (phones AND tablets since 2026-08-06),
 * a lesson whose target sits in a closed toolbar tray would pulse
 * something display:none. Prepend a step that opens the tray — the
 * same tap the user would make. Desktop (all groups visible) returns
 * null, as does a tray that's already open or missing (the map's
 * toolbar drops the View tool — its band is out in the open there).
 */
function trayStep(lesson) {
  if (!lesson.tool || !window.matchMedia?.(TRAY_TOOLBAR).matches) return null;
  const group = document.querySelector(`[data-tool-group="${lesson.tool}"]`);
  if (!group || group.classList.contains('is-open')) return null;
  const label = lesson.tool === 'filter' ? 'Filter' : 'View';
  return { target: `[data-tool="${lesson.tool}"]`, hint: `Tap ${label} to open its tools` };
}

function triggerPulseInner(lesson) {
  let steps = lesson.steps?.length ? lesson.steps : [{ target: lesson.target, hint: lesson.how }];
  const tray = trayStep(lesson);
  if (tray) steps = [tray, ...steps];
  if (lesson.requiresCard && !document.querySelector(steps[0].target)) {
    // No card to point at: the feed may still be loading (we just
    // flipped off the map) or the area-scoped feed settled empty —
    // "Nothing yoinked around here yet" with zero cards to tap (iPad
    // report 2026-07-26). Poll for a card; if the empty state shows,
    // widen to everywhere — the very tap its copy suggests.
    pulseSequence(steps, { pollFirst: true, widenIfEmpty: true });
    return;
  }
  if (steps.length > 1) {
    pulseSequence(steps);
    return;
  }
  if (!document.querySelector(steps[0].target)) {
    showToast(lesson.how, { type: 'info' });
    return;
  }
  pulseElement(steps[0].target);
}

/**
 * Brass-glow pulse on an element until it's clicked (30s safety stop).
 * Returns a cleanup function.
 *
 * The glow survives re-renders: views rebuild their innerHTML as data
 * settles (the journal re-renders after every fetch), which replaces
 * the pulsed node with a class-less twin — the share_profile lesson's
 * step 2 never visibly blinked (iPad report 2026-07-26). A keep-alive
 * re-acquires the selector and moves the glow to the successor.
 */
function pulseElement(selector) {
  let el = document.querySelector(selector);
  if (!el) return () => {};

  el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  let stopped = false;
  const startTimer = setTimeout(() => el.classList.add('tutorial-pulse'), 350);
  el.addEventListener('click', stopPulse, { once: true });

  const keepAlive = setInterval(() => {
    if (stopped || el.isConnected) return;
    const next = document.querySelector(selector);
    if (!next) return; // mid re-render — try again next tick
    el.removeEventListener('click', stopPulse);
    el = next;
    el.classList.add('tutorial-pulse');
    el.addEventListener('click', stopPulse, { once: true });
  }, 500);

  function stopPulse() {
    if (stopped) return;
    stopped = true;
    clearTimeout(startTimer);
    clearTimeout(safetyTimer);
    clearInterval(keepAlive);
    el.classList.remove('tutorial-pulse');
    el.removeEventListener('click', stopPulse);
  }
  const safetyTimer = setTimeout(stopPulse, 30000);
  return stopPulse;
}

let activeSequenceCleanup = null;

/**
 * Chain through a multi-step pulse sequence: pulse each target until
 * clicked, then poll for the next (async-rendered) target and repeat.
 * pollFirst: poll for step 0 too instead of requiring it up front.
 * widenIfEmpty: while polling, if the feed settles on its empty state
 * with a "Show everywhere" way out, take it (once) and keep polling.
 */
function pulseSequence(steps, { pollFirst = false, widenIfEmpty = false } = {}) {
  activeSequenceCleanup?.();

  let cancelled = false;
  let pollTimer = null;
  let stopCurrentPulse = null;
  let widened = false;
  const safetyTimer = setTimeout(cleanup, 60000);

  function cleanup() {
    cancelled = true;
    clearInterval(pollTimer);
    clearTimeout(safetyTimer);
    stopCurrentPulse?.();
    activeSequenceCleanup = null;
  }
  activeSequenceCleanup = cleanup;

  function advanceToStep(idx) {
    if (cancelled || idx >= steps.length) return;
    if (idx === 0 && !pollFirst) {
      if (!document.querySelector(steps[0].target)) {
        showToast(steps[0].hint, { type: 'info' });
        cleanup();
        return;
      }
      startStep(idx);
    } else {
      pollForStep(idx);
    }
  }

  function pollForStep(idx) {
    const step = steps[idx];
    let attempts = 0;
    let foundOnce = false;
    pollTimer = setInterval(() => {
      if (cancelled) return;
      attempts++;
      const el = document.querySelector(step.target);
      if (el && !foundOnce) {
        // Appeared, but the view may still be re-rendering (feeds rebuild
        // after data loads) — wait one extra cycle for the DOM to settle
        foundOnce = true;
      } else if (el && foundOnce) {
        clearInterval(pollTimer);
        startStep(idx);
      } else if (!el && foundOnce) {
        foundOnce = false;
      } else if (widenIfEmpty && !widened && document.querySelector('.v2-empty')
          && document.querySelector('[data-clear-bounds]')) {
        // The feed settled empty inside its area scope — no card will
        // ever appear. Tap Show everywhere for them and give the wider
        // load a fresh polling window.
        widened = true;
        attempts = 0;
        document.querySelector('[data-clear-bounds]').click();
      } else if (attempts >= 30) { // 30 × 250ms = 7.5s
        clearInterval(pollTimer);
        showToast(step.hint, { type: 'info' });
        cleanup();
      }
    }, 250);
  }

  function startStep(idx) {
    const selector = steps[idx].target;
    stopCurrentPulse = pulseElement(selector);
    if (idx < steps.length - 1) {
      // Delegate on document so the listener survives view re-renders
      const onStepClick = (e) => {
        if (cancelled) {
          document.removeEventListener('click', onStepClick, true);
          return;
        }
        if (e.target.closest(selector)) {
          document.removeEventListener('click', onStepClick, true);
          advanceToStep(idx + 1);
        }
      };
      document.addEventListener('click', onStepClick, true);
    }
  }

  advanceToStep(0);
}

/** Test hook. */
export function _reset() {
  activeSequenceCleanup?.();
  coachMarkEl?.remove();
  coachMarkEl = null;
}
